import { checkIsArrayEmpty } from "../../../../../../utils/common/dataTypes/array";
import { checkIsStringEmpty } from "../../../../../Common/modules/DataTypes/utils/strings";
import {
  QUESTIONNAIRE_TYPES,
  QUESTION_TYPES,
} from "../ExamFormContent.constants";
import { QUESTIONNAIRE_ANSWER_KEYS } from "./Questionnaire.constants";
import { IQuestionnaire } from "./useQuestionnaire.interfaces";

/**
 * this function is used to reduce array of exam questions into an object with key value pairs
 * used in exly-creator-app/RecordedContent
 * @param questions - array of questions for an exam
 * @returns {object} - An object containing question's question_uids as keys and its details as values
 */
export const getInitialAnswers = (
  questions: IQuestionnaire.IQuestion[],
): IQuestionnaire.IAnswers => {
  return (questions || []).reduce((answers, question) => {
    const { question_type: questionType, question_uid: questionUid } = question;

    const isMultiSelect = questionType === QUESTION_TYPES.MULTI_SELECT;

    return {
      ...answers,
      [questionUid]: {
        [QUESTIONNAIRE_ANSWER_KEYS.answer]: isMultiSelect ? undefined : null,
        [QUESTIONNAIRE_ANSWER_KEYS.selected_options_uid_list]: isMultiSelect
          ? []
          : undefined,
        [QUESTIONNAIRE_ANSWER_KEYS.question_type]: questionType,
        [QUESTIONNAIRE_ANSWER_KEYS.question_uid]: questionUid,
      },
    };
  }, {});
};

/**
 * this function is used to reduce array of form questions into an object with key value pairs
 * used in exly-creator-app/RecordedContent
 * @param questionResponses - array of questions with answers for a form
 * @returns {object} - An object containing question's question_uids as keys and its details as values
 */
export const parseAnswers = (
  questionResponses: Array<IQuestionnaire.IFormQuestionResponse> = [],
) => {
  return questionResponses.reduce((prev, answer) => {
    const {
      answer: inputAnswer,
      question_type: questionType,
      options,
    } = answer;

    const isOptionsType =
      questionType === QUESTION_TYPES.SINGLE_SELECT ||
      questionType === QUESTION_TYPES.MULTI_SELECT;
    const hasAnswer = isOptionsType
      ? !checkIsArrayEmpty(options)
      : inputAnswer !== undefined && inputAnswer !== null;

    if (!hasAnswer) return prev;

    let parsedAnswer;
    switch (questionType) {
      case QUESTION_TYPES.SINGLE_SELECT:
        parsedAnswer = options?.find((option) => option.is_selected)
          ?.option_uid;
        break;
      case QUESTION_TYPES.MULTI_SELECT:
        parsedAnswer = (options || [])
          .filter((option) => option.is_selected)
          .map((option) => option.option_uid);
        break;
      case QUESTION_TYPES.CALENDAR:
        parsedAnswer = inputAnswer ? new Date(inputAnswer) : undefined;
        break;
      case QUESTION_TYPES.STAR_RATING:
      case QUESTION_TYPES.TEN_NUMBER_RATING:
      case QUESTION_TYPES.FIVE_NUMBER_RATING:
        parsedAnswer = Number(inputAnswer || 0);
        break;
      default:
        parsedAnswer = inputAnswer;
    }

    const answerKey =
      questionType === QUESTION_TYPES.MULTI_SELECT
        ? QUESTIONNAIRE_ANSWER_KEYS.selected_options_uid_list
        : QUESTIONNAIRE_ANSWER_KEYS.answer;

    return {
      ...prev,
      [answer.question_uid]: { ...answer, [answerKey]: parsedAnswer },
    };
  }, {});
};

/**
 * validators object containing validation function for form.
 * used in exly-creator-app/RecordedContent
 */
export const validators = {
  form: (
    answers?: IQuestionnaire.IAnswers,
    questions?: Array<IQuestionnaire.IQuestion>,
  ) => {
    const mandatoryQuestions = questions?.filter(
      (question) => question.is_mandatory,
    );
    const isAllMandatoryQuestionsFilled = mandatoryQuestions?.reduce(
      (isValid, question) => {
        const { question_uid: questionUid } = question;
        const {
          question_type: questionType,
          answer: inputAnswer,
          selected_options_uid_list: selectedOptionsUidList,
        } = answers?.[questionUid] || {};
        const hasAnswer =
          questionType === QUESTION_TYPES.MULTI_SELECT
            ? !checkIsArrayEmpty(selectedOptionsUidList)
            : !checkIsStringEmpty(inputAnswer?.toString());
        return isValid && hasAnswer;
      },
      true,
    );

    return {
      isAllMandatoryQuestionsFilled,
    };
  },
};

/**
 * Checks if the given questionnaire type corresponds to an Exam questionnaire.
 * used in exly-creator-app/RecordedContent
 * @param {number} type - The questionnaire type to be checked.
 * @returns {boolean} - True if the type corresponds to an Exam questionnaire, false otherwise.
 */
export const isExamQuestionnaire = (type?: number) =>
  type === QUESTIONNAIRE_TYPES.EXAM;

/**
 * Checks if the given questionnaire type corresponds to a Form questionnaire.
 * used in exly-creator-app/RecordedContent
 * @param {number} type - The questionnaire type to be checked.
 * @returns {boolean} - True if the type corresponds to a Form questionnaire, false otherwise.
 */
export const isFormQuestionnaire = (type?: number) =>
  type === QUESTIONNAIRE_TYPES.FORM;
